import axios from 'axios';
import {
  QuoteResponse,
  SignalsResponse,
  SignalsQueryParams,
  SignalDetailResponse,
  EventsResponse,
  EventsQueryParams,
  CatalystEvent,
  WatchlistResponse,
  WatchlistItem,
  CreateWatchlistItemRequest,
  UpdateWatchlistItemRequest,
  AlertsResponse,
  PriceAlert,
  CreateAlertRequest,
  AlertStatus,
  DecorateRequest,
  DecorateResponse,
} from '@catalyst-iq/shared';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000, // הוגדל מ-10000 ל-30000 בגלל בעיות איטיות רשת (Timeout) כפי שראינו ב-EventFeed
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.response.use(
  (response) => {
    console.log(`[API Success] ${response.config.method?.toUpperCase()} ${response.config.url}:`, response.data);
    return response;
  },
  (err) => { 
    console.error('API Error:', err); 
    return Promise.reject(err); 
  }
);

export const api = {
  // ── Health ──────────────────────────────────────────────────
  health: () =>
    apiClient.get('/health').then((r) => r.data),

  // ── Discovery ───────────────────────────────────────────────

  getQuote: (ticker: string): Promise<QuoteResponse> =>
    apiClient.get(`/quote/${encodeURIComponent(ticker)}`).then((r) => r.data),

  getSignals: (params?: SignalsQueryParams): Promise<SignalsResponse> =>
    apiClient.get('/signals', { params }).then((r) => r.data),

  getSignal: (ticker: string): Promise<SignalDetailResponse> =>
    apiClient.get(`/signals/${encodeURIComponent(ticker)}`).then((r) => r.data),

  // ── Events ──────────────────────────────────────────────────

  getEvents: (params?: EventsQueryParams): Promise<EventsResponse> =>
    apiClient.get('/events', { params }).then((r) => r.data),

  getEventContentById: async (id: string): Promise<{ full_content: string }> => {
    try {
      const response = await apiClient.get('/events/content/by-id', { params: { id } });
      return response.data;
    } catch (error: any) {
      console.error('[API Service] getEventContentById failed:', error.message);
      return { full_content: 'The article content is temporarily unavailable. Please try opening the original source link.' };
    }
  },
  getEvent: (id: string): Promise<{ event: CatalystEvent }> =>
    apiClient.get(`/events/${encodeURIComponent(id)}`).then((r) => r.data),

  decorateEvents: (payload: DecorateRequest): Promise<DecorateResponse> =>
    apiClient.post('/events/decorate', payload).then((r) => r.data),

  // ── Tracking — Watchlist ─────────────────────────────────────

  getWatchlist: (): Promise<WatchlistResponse> =>
    apiClient.get('/watchlist').then((r) => r.data),

  /** Live quotes + sparklines for the current watchlist (slow path). */
  getWatchlistEnrich: (): Promise<WatchlistResponse> =>
    apiClient.get('/watchlist/enrich').then((r) => r.data),

  addToWatchlist: (body: CreateWatchlistItemRequest): Promise<WatchlistItem> =>
    apiClient.post('/watchlist', body).then((r) => r.data),

  updateWatchlistItem: (id: string, body: UpdateWatchlistItemRequest): Promise<WatchlistItem> =>
    apiClient.put(`/watchlist/${id}`, body).then((r) => r.data),

  removeFromWatchlist: (id: string): Promise<void> =>
    apiClient.delete(`/watchlist/${id}`).then(() => undefined),

  // ── Tracking — Alerts ────────────────────────────────────────

  getAlerts: (status?: AlertStatus): Promise<AlertsResponse> =>
    apiClient.get('/alerts', { params: status ? { status } : undefined }).then((r) => r.data),

  createAlert: (body: CreateAlertRequest): Promise<PriceAlert> =>
    apiClient.post('/alerts', body).then((r) => r.data),

  cancelAlert: (id: string): Promise<void> =>
    apiClient.delete(`/alerts/${id}`).then(() => undefined),
};